import { useTranslation } from "react-i18next"

import type { HostedAgentFormErrors, HostedAgentFormState } from "../utils/hosted-agents"

import { AlertTriangle } from "@/components/icons"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"

import { useAdminBudgetModelQuery } from "../hooks/admin-queries"
import { HOSTED_AGENT_ALERT_THRESHOLD } from "../utils/hosted-agents"

// Form tabs of the hosted-agent edit wizard (HERMES Fas 5). Each tab is a
// controlled component over the shared HostedAgentFormState owned by
// EditHostedAgentDialog: `form` in, `patch` (partial merge) out. The Model
// and Tools tabs live in their own files (hosted-agent-model-tab.tsx,
// hosted-agent-tools-tab.tsx).

type TabProps = {
  form: HostedAgentFormState
  patch: (partial: Partial<HostedAgentFormState>) => void
  errors: HostedAgentFormErrors
}

export { HostedAgentProfileTab } from "./hosted-agent-profile-tab"

export function HostedAgentInstructionsTab({ form, patch }: TabProps) {
  const { t } = useTranslation()
  return (
    <div className="space-y-2">
      <Label htmlFor="hosted-agent-instructions">
        {t("adminDashboard.hostedAgents.instructionsTab.label", { defaultValue: "Instructions" })}
      </Label>
      <Textarea
        className="font-mono text-xs leading-relaxed"
        id="hosted-agent-instructions"
        rows={16}
        value={form.instructions}
        placeholder={t("adminDashboard.hostedAgents.instructionsTab.placeholder", {
          defaultValue: "# Role\nYou are…",
        })}
        onChange={(event) => patch({ instructions: event.target.value })}
      />
      <div className="text-muted-foreground flex justify-between text-xs">
        <span>
          {t("adminDashboard.hostedAgents.instructionsTab.help", {
            defaultValue:
              "Durable rules for the agent, written in markdown. They are layered on top of the platform baseline prompt.",
          })}
        </span>
        <span>
          {t("adminDashboard.hostedAgents.instructionsTab.counter", {
            defaultValue: "{{count}} characters",
            count: form.instructions.length,
          })}
        </span>
      </div>
    </div>
  )
}

export { HostedAgentSkillsTab } from "./hosted-agent-skills-tab"

export function HostedAgentMemoryTab({ form, patch }: TabProps) {
  const { t } = useTranslation()
  return (
    <div className="space-y-4">
      <RadioGroup
        value={form.memoryMode}
        onValueChange={(value) =>
          patch({ memoryMode: value as HostedAgentFormState["memoryMode"] })
        }
      >
        <label
          className="flex cursor-pointer items-start gap-3 rounded-md border p-3"
          htmlFor="hosted-agent-memory-personal"
        >
          <RadioGroupItem className="mt-0.5" id="hosted-agent-memory-personal" value="personal" />
          <span className="space-y-0.5">
            <span className="block text-sm font-medium">
              {t("adminDashboard.hostedAgents.memoryTab.personalLabel", {
                defaultValue: "Personal",
              })}
            </span>
            <span className="text-muted-foreground block text-xs">
              {t("adminDashboard.hostedAgents.memoryTab.personalHelp", {
                defaultValue: "Each user only sees their own threads with the agent.",
              })}
            </span>
          </span>
        </label>
        <label
          className="flex cursor-pointer items-start gap-3 rounded-md border p-3"
          htmlFor="hosted-agent-memory-open"
        >
          <RadioGroupItem className="mt-0.5" id="hosted-agent-memory-open" value="open" />
          <span className="space-y-0.5">
            <span className="block text-sm font-medium">
              {t("adminDashboard.hostedAgents.memoryTab.openLabel", { defaultValue: "Open" })}
            </span>
            <span className="text-muted-foreground block text-xs">
              {t("adminDashboard.hostedAgents.memoryTab.openHelp", {
                defaultValue:
                  "Threads are shared — everyone with access to the agent sees the team's threads.",
              })}
            </span>
          </span>
        </label>
      </RadioGroup>
      <p className="text-muted-foreground rounded-md border border-dashed p-3 text-xs">
        {t("adminDashboard.hostedAgents.memoryTab.stampNote", {
          defaultValue:
            "Threads are stamped with the memory mode at creation: new threads follow the new mode, existing threads keep the mode they were created with.",
        })}
      </p>
    </div>
  )
}

export function HostedAgentBudgetTab({ form, patch, errors }: TabProps) {
  const { t } = useTranslation()
  const invalidNumber = t("adminDashboard.hostedAgents.budgetTab.invalidNumber", {
    defaultValue: "Enter a number of 0 or more.",
  })
  // Warn when "switch to the budget model" is selected but no global budget
  // model is mapped (GET /api/admin/models/budget): the backend then degrades
  // the downgrade to an effort clamp on the original model.
  const wantsBudgetModel = form.budgetOnExceeded === "budget_model"
  const budgetModelQuery = useAdminBudgetModelQuery(wantsBudgetModel)
  const budgetModelMissing =
    wantsBudgetModel && budgetModelQuery.isSuccess && !budgetModelQuery.data
  return (
    <div className="space-y-4">
      <p className="text-muted-foreground text-sm">
        {t("adminDashboard.hostedAgents.budgetTab.help", {
          defaultValue:
            "Budgets are required — a hosted agent can never run without spend limits. Defaults are prefilled; adjust them to the customer's plan.",
        })}
      </p>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="hosted-agent-budget-daily">
            {t("adminDashboard.hostedAgents.budgetTab.dailyTokensLabel", {
              defaultValue: "Daily token budget",
            })}{" "}
            <span className="text-foreground">*</span>
          </Label>
          <Input
            aria-invalid={Boolean(errors.budgetDailyTokens)}
            id="hosted-agent-budget-daily"
            inputMode="numeric"
            value={form.budgetDailyTokens}
            aria-describedby={
              errors.budgetDailyTokens ? "hosted-agent-budget-daily-error" : undefined
            }
            onChange={(event) => patch({ budgetDailyTokens: event.target.value })}
          />
          {errors.budgetDailyTokens ? (
            <p
              className="bg-destructive text-destructive-foreground border-foreground rounded-md border px-2 py-1 text-xs"
              id="hosted-agent-budget-daily-error"
            >
              {invalidNumber}
            </p>
          ) : null}
        </div>

        <div className="space-y-2">
          <Label htmlFor="hosted-agent-budget-monthly">
            {t("adminDashboard.hostedAgents.budgetTab.monthlyTokensLabel", {
              defaultValue: "Monthly token budget",
            })}{" "}
            <span className="text-foreground">*</span>
          </Label>
          <Input
            aria-invalid={Boolean(errors.budgetMonthlyTokens)}
            id="hosted-agent-budget-monthly"
            inputMode="numeric"
            value={form.budgetMonthlyTokens}
            aria-describedby={
              errors.budgetMonthlyTokens ? "hosted-agent-budget-monthly-error" : undefined
            }
            onChange={(event) => patch({ budgetMonthlyTokens: event.target.value })}
          />
          {errors.budgetMonthlyTokens ? (
            <p
              className="bg-destructive text-destructive-foreground border-foreground rounded-md border px-2 py-1 text-xs"
              id="hosted-agent-budget-monthly-error"
            >
              {invalidNumber}
            </p>
          ) : null}
        </div>

        <div className="space-y-2">
          <Label htmlFor="hosted-agent-budget-sek">
            {t("adminDashboard.hostedAgents.budgetTab.monthlySekLabel", {
              defaultValue: "Monthly budget (SEK)",
            })}{" "}
            <span className="text-foreground">*</span>
          </Label>
          <Input
            aria-describedby={errors.budgetMonthlySek ? "hosted-agent-budget-sek-error" : undefined}
            aria-invalid={Boolean(errors.budgetMonthlySek)}
            id="hosted-agent-budget-sek"
            inputMode="decimal"
            value={form.budgetMonthlySek}
            onChange={(event) => patch({ budgetMonthlySek: event.target.value })}
          />
          {errors.budgetMonthlySek ? (
            <p
              className="bg-destructive text-destructive-foreground border-foreground rounded-md border px-2 py-1 text-xs"
              id="hosted-agent-budget-sek-error"
            >
              {invalidNumber}
            </p>
          ) : null}
        </div>

        <div className="space-y-2">
          <Label htmlFor="hosted-agent-budget-exceeded">
            {t("adminDashboard.hostedAgents.budgetTab.onExceededLabel", {
              defaultValue: "When the budget is exceeded",
            })}
          </Label>
          <Select
            value={form.budgetOnExceeded}
            onValueChange={(value) =>
              patch({ budgetOnExceeded: value as HostedAgentFormState["budgetOnExceeded"] })
            }
          >
            <SelectTrigger id="hosted-agent-budget-exceeded">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="block">
                {t("adminDashboard.hostedAgents.budgetTab.onExceededBlock", {
                  defaultValue: "Block new runs",
                })}
              </SelectItem>
              <SelectItem value="budget_model">
                {t("adminDashboard.hostedAgents.budgetTab.onExceededBudgetModel", {
                  defaultValue: "Switch to the budget model",
                })}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="hosted-agent-budget-alert-threshold">
            {t("adminDashboard.hostedAgents.budgetTab.alertThresholdLabel", {
              defaultValue: "Warn at (% of budget)",
            })}
          </Label>
          <Input
            aria-invalid={Boolean(errors.budgetAlertThresholdPercent)}
            id="hosted-agent-budget-alert-threshold"
            inputMode="numeric"
            max={HOSTED_AGENT_ALERT_THRESHOLD.max}
            min={HOSTED_AGENT_ALERT_THRESHOLD.min}
            type="number"
            value={form.budgetAlertThresholdPercent}
            aria-describedby={
              errors.budgetAlertThresholdPercent
                ? "hosted-agent-budget-alert-threshold-error"
                : "hosted-agent-budget-alert-threshold-help"
            }
            onChange={(event) => patch({ budgetAlertThresholdPercent: event.target.value })}
          />
          {errors.budgetAlertThresholdPercent ? (
            <p
              className="bg-destructive text-destructive-foreground border-foreground rounded-md border px-2 py-1 text-xs"
              id="hosted-agent-budget-alert-threshold-error"
            >
              {t("adminDashboard.hostedAgents.budgetTab.alertThresholdInvalid", {
                defaultValue: "Enter a whole number between {{min}} and {{max}}.",
                min: HOSTED_AGENT_ALERT_THRESHOLD.min,
                max: HOSTED_AGENT_ALERT_THRESHOLD.max,
              })}
            </p>
          ) : (
            <p
              className="text-muted-foreground text-xs"
              id="hosted-agent-budget-alert-threshold-help"
            >
              {t("adminDashboard.hostedAgents.budgetTab.alertThresholdHelp", {
                defaultValue:
                  "An alert is raised once per window when usage reaches this share of a cap; a second alert follows at 100 %.",
              })}
            </p>
          )}
        </div>
      </div>

      {budgetModelMissing ? (
        <Alert>
          <AlertTriangle className="size-4" />
          <AlertTitle>
            {t("adminDashboard.hostedAgents.budgetTab.noBudgetModelTitle", {
              defaultValue: "No budget model configured",
            })}
          </AlertTitle>
          <AlertDescription>
            {t("adminDashboard.hostedAgents.budgetTab.noBudgetModelDescription", {
              defaultValue:
                "No global budget model is mapped, so exceeding the budget will only lower the reasoning effort on the agent's current model. Set the budget model mapping (PUT /api/admin/models/budget) to enable the downgrade.",
            })}
          </AlertDescription>
        </Alert>
      ) : null}
    </div>
  )
}
